import { io, Socket } from "socket.io-client";

class WebSocketApiService {
    private socket: Socket | null = null;
    private url: string | null = null;
    private reconnectInterval: number = 5000; // 5 seconds
    private reconnectAttempts: number = 0;
    private maxReconnectAttempts: number = 5;
    private messageHandlers: ((data: any) => void)[] = [];

    constructor() { }

    // Getter to access socket instance
    public getSocket(): Socket | null {
        return this.socket;
    }

    public init(url: string): void {
        // ✅ Prevent multiple connections
        if (this.socket && this.socket.connected) {
            console.warn("⚠️ WebSocket already connected.");
            return;
        }

        this.url = url;
        this.connect();
    }

    private connect(): void {
        if (!this.url) {
            console.error("❌ WebSocket URL not set.");
            return;
        }

        console.log("🔌 Connecting to socket server...");

        // Get userId from localStorage
        const userId = localStorage.getItem('userId');

        this.socket = io(this.url, {
            transports: ["websocket"],
            reconnectionAttempts: this.maxReconnectAttempts,
            reconnectionDelay: this.reconnectInterval,
            path: "/socket.io/",
            forceNew: true,
            autoConnect: true
        });

        this.socket.on("connect", () => {
            console.log("✅ Socket.io connected.");
            this.reconnectAttempts = 0;

            // Get userId from localStorage and emit register event
            const userId = localStorage.getItem('userId');
            if (userId && this.socket) {
                this.socket.emit('register', { userId });
            }
        });

        this.socket.on("disconnect", () => {
            console.warn("❌ Socket.io disconnected.");
        });

        this.socket.on("connect_error", (error) => {
            console.error("⚠️ Socket.io connection error:", error);
        });

        this.socket.on("message", (data) => {
            this.messageHandlers.forEach((handler) => handler(data));
        });
    }

    public sendMessage(message: any): void {
        if (this.socket && this.socket.connected) {
            this.socket.emit("message", message);
        } else {
            console.error("Cannot send message: Socket.io not connected.");
        }
    }

    public onMessage(handler: (data: any) => void): void {
        this.messageHandlers.push(handler);
    }

    public removeMessageHandler(handler: (data: any) => void): void {
        this.messageHandlers = this.messageHandlers.filter((h) => h !== handler);
    }

    public close(): void {
        if (this.socket) {
            console.log("🛑 Closing socket connection...");
            this.socket.disconnect();
            this.socket = null;  // ✅ Reset socket reference
        }
    }

    public isConnected(): boolean {
        return this.socket !== null && this.socket.connected;
    }

    public isConnectedAndReady(): boolean {
        return this.isConnected();
    }
}

export const webSocketApiService = new WebSocketApiService();